import React, { useMemo } from "react";
import { Layer, Source } from "react-map-gl";
import { RouteId } from "../../../generated/gpb/api/id";
import { useOpenRoadsPoints } from "./hooks/useOpenRoadsPoints";
import { useOpenRoadsGeoJSON } from "./hooks/useOpenRoadsGeoJson";
import { useOpenRoadsIconLoader } from "./hooks/useOpenRoadsIconLoader";
import { useOpenRoadsPopup } from "./hooks/useOpenRoadsPopup";
import { useOpenRoadsVisibility } from "../../../features/mapDisplay/mapDisplaySlice";
import OpenRoadsPopup from "../OpenRoadsPopup";

type Props = {
    routeId: RouteId,
    totalPoints: number | undefined,
};

export const OpenRoadsGeoMarker: React.FC<Props> = ( { routeId, totalPoints } ) =>
{
    const shouldShowMarkers = useOpenRoadsVisibility();
    const { points, loading } = useOpenRoadsPoints( routeId, totalPoints );
    const geojson = useOpenRoadsGeoJSON( points );
    const { popup, setPopup, hoverTimeout } = useOpenRoadsPopup();
    useOpenRoadsIconLoader();

    const openRoadsLayer = useMemo( () => ( {
        id: "open-roads-layer",
        type: "symbol" as const,
        source: "open-roads-source",
        layout: {
            "icon-image": "openRoadsIcon",
            "icon-size": 0.3,
            "icon-allow-overlap": true,
        },
    } ), [] );

    if ( !shouldShowMarkers || loading || !geojson.features.length ) return null;

    return (
        <React.Fragment>
            <Source id = "open-roads-source" type = "geojson" data = {geojson}>
                <Layer {...openRoadsLayer}/>
            </Source>

            {popup && (
                <OpenRoadsPopup
                    latitude = {popup.latitude}
                    longitude = {popup.longitude}
                    text = {popup.text}
                    description = {popup.description}
                    imageUrl = {popup.imageUrl}
                    copyAddress = {popup.copyAddress}
                    onMouseLeave = {() =>
                    {
                        if ( hoverTimeout.current ) clearTimeout( hoverTimeout.current );
                        hoverTimeout.current = setTimeout( () =>
                        {
                            setPopup( null );
                        }, 50 );
                    }}
                    hoverTimeoutRef = {hoverTimeout}
                />
            )}
        </React.Fragment>
    );
};
